import {
	OrderItemStatus,
	OrderShippingMethod,
	useCreateOrderShipmentMutation,
	useGetShippingCarriersQuery,
} from '@codezo/graphql';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { DatePicker } from '../ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { Field } from '../ui/field';
import { Input } from '../ui/input';
import { Radio } from '../ui/radio';
import { SHIPPING_METHOD_LABEL } from '../../lib/status-configs';
import { cn } from '../../lib/utils';

/**
 * Can this item go in a box yet? Exhaustive, so a new status has to be answered
 * here rather than falling into whichever branch happens to be last.
 *
 * The API decides for real — it rejects anything still in production — but the
 * form has to say why a product can't be picked without a round trip.
 */
const CAN_SHIP: Record<OrderItemStatus, boolean> = {
	[OrderItemStatus.Pending]: false,
	[OrderItemStatus.InProduction]: false,
	[OrderItemStatus.ProductionCompleted]: true,
	[OrderItemStatus.PartiallyShipped]: true,
	[OrderItemStatus.Shipped]: true,
	[OrderItemStatus.Delivered]: false,
	[OrderItemStatus.Cancelled]: false,
};

const BLOCKED_REASON: Record<OrderItemStatus, string> = {
	[OrderItemStatus.Pending]: 'Not in production yet',
	[OrderItemStatus.InProduction]: 'Still in production',
	[OrderItemStatus.ProductionCompleted]: '',
	[OrderItemStatus.PartiallyShipped]: '',
	[OrderItemStatus.Shipped]: '',
	[OrderItemStatus.Delivered]: 'Already delivered',
	[OrderItemStatus.Cancelled]: 'Cancelled',
};

export interface ShippableItem {
	id: string;
	productName?: string | null;
	quantity: number;
	/** Counted from shipment lines. Null means not computable, NOT zero. */
	shippedQuantity?: number | null;
	status: OrderItemStatus;
	/** The method the client bought for this product — the pre-fill, not a rule. */
	delivery?: { shippingMethod: OrderShippingMethod } | null;
}

interface RecordShipmentModalProps {
	open: boolean;
	orderId: string;
	items: ShippableItem[];
	onClose: () => void;
	onSaved: () => void;
}

/** What is left to ship. A null `shippedQuantity` means the API couldn't count it,
 * so treat none as shipped rather than silently hiding the product. */
function remainingOf(item: ShippableItem) {
	return Math.max(0, item.quantity - (item.shippedQuantity ?? 0));
}

/** Shared with the page, so the button that opens this and the list inside it
 * can never disagree about what is shippable. */
export function canGoInBox(item: ShippableItem) {
	return remainingOf(item) > 0 && CAN_SHIP[item.status];
}

/**
 * Records one shipment: what is in the box, how it travels, and its tracking.
 *
 * Opens pre-filled with one shipment per shipping method over everything still
 * unshipped — two methods mean the client bought two speeds, which is real
 * information and a sensible box boundary. It is only a starting point: the
 * admin adds, removes and re-quantifies before recording, because the factory,
 * not the quote, decides what actually fits in a box.
 *
 * One call records one box. Anything left over stays unshipped and the modal can
 * be opened again for the next one.
 */
export function RecordShipmentModal({
	open,
	orderId,
	items,
	onClose,
	onSaved,
}: RecordShipmentModalProps) {
	const { data: carrierData } = useGetShippingCarriersQuery({ fetchPolicy: 'no-cache' });
	const carriers = carrierData?.getShippingCarriers ?? [];

	const shippable = useMemo(() => items.filter(canGoInBox), [items]);
	const notReady = useMemo(
		() => items.filter((i) => remainingOf(i) > 0 && !CAN_SHIP[i.status]),
		[items]
	);

	// Methods in the order they appear, so the default group is the first thing
	// the admin sees on the page rather than whichever enum sorts first.
	const methods = useMemo(() => {
		const seen: Array<OrderShippingMethod | null> = [];
		for (const item of shippable) {
			const method = item.delivery?.shippingMethod ?? null;
			if (!seen.includes(method)) seen.push(method);
		}
		return seen;
	}, [shippable]);

	const [method, setMethod] = useState<OrderShippingMethod | null>(methods[0] ?? null);
	const prefill = (forMethod: OrderShippingMethod | null) =>
		Object.fromEntries(
			shippable
				.filter((i) => (i.delivery?.shippingMethod ?? null) === forMethod)
				.map((i) => [i.id, remainingOf(i)])
		);
	const [lines, setLines] = useState<Record<string, number>>(() => prefill(methods[0] ?? null));

	const [carrier, setCarrier] = useState('');
	const [trackingNumber, setTrackingNumber] = useState('');
	const [shippedAt, setShippedAt] = useState<Date | null>(null);
	const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState<Date | null>(null);

	const [save, { loading }] = useCreateOrderShipmentMutation();

	// Switching method re-proposes that group. It replaces the selection rather
	// than adding to it: the method is a property of the whole box, so anything
	// already picked would be travelling a way it wasn't chosen for.
	const pickMethod = (next: OrderShippingMethod | null) => {
		setMethod(next);
		setLines(prefill(next));
	};

	const toggle = (item: ShippableItem) =>
		setLines((prev) => {
			const next = { ...prev };
			if (next[item.id] != null) delete next[item.id];
			else next[item.id] = remainingOf(item);
			return next;
		});

	const setQuantity = (item: ShippableItem, raw: string) => {
		const parsed = Number.parseInt(raw, 10);
		setLines((prev) => ({
			...prev,
			[item.id]: Number.isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), remainingOf(item)),
		}));
	};

	const chosen = shippable.filter((i) => (lines[i.id] ?? 0) > 0);
	const totalPieces = chosen.reduce((sum, i) => sum + (lines[i.id] ?? 0), 0);

	const handleSave = async () => {
		if (chosen.length === 0) return;
		try {
			const { data } = await save({
				variables: {
					input: {
						orderId,
						lines: chosen.map((i) => ({ orderItemId: i.id, quantity: lines[i.id] })),
						// Omitted, not null, when no method was quoted — the goods
						// still move and still need tracking.
						shippingMethod: method ?? undefined,
						carrier: carrier.trim() || undefined,
						trackingNumber: trackingNumber.trim() || undefined,
						shippedAt: shippedAt?.toISOString(),
						estimatedDeliveryDate: estimatedDeliveryDate?.toISOString(),
					},
				},
			});
			const result = data?.createOrderShipment;
			if (result?.success) {
				toast.success(result.message || 'Shipment recorded');
				onSaved();
				onClose();
			} else {
				toast.error(result?.message || 'Could not record the shipment.');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not record the shipment.');
		}
	};

	return (
		<Dialog open={open} onOpenChange={(next) => !next && onClose()}>
			<DialogContent className="max-w-[700px]">
				<DialogHeader>
					<DialogTitle>Record shipment</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className="flex flex-col gap-4">
						{shippable.length === 0 ? (
							<div className="rounded-lg border border-line-1 bg-surface px-3 py-4 text-sm text-text-3">
								Nothing is ready to ship. Products can go in a box once their
								production is complete.
							</div>
						) : (
							<>
								<Field label="Shipping method">
									<div className="flex flex-wrap gap-1.5">
										{methods.map((m) => {
											const active = m === method;
											return (
												<button
													key={m ?? 'none'}
													type="button"
													onClick={() => pickMethod(m)}
													className={cn(
														'flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-sm font-medium',
														active ? 'text-text-1' : 'text-text-3'
													)}
													style={{
														borderColor: active
															? 'var(--accent)'
															: 'var(--line-2)',
														background: active
															? 'var(--accent-soft)'
															: 'transparent',
													}}
												>
													<Radio checked={active} />
													{m ? SHIPPING_METHOD_LABEL[m] : 'No method'}
												</button>
											);
										})}
									</div>
									{methods.length > 1 && (
										<div className="mt-1.5 text-xs text-text-4">
											Two methods means the client bought two speeds. Picking
											one proposes just those products — adjust below.
										</div>
									)}
								</Field>

								<Field label="What is in the box">
									<div className="overflow-hidden rounded-lg border border-line-1">
										{shippable.map((item, i) => {
											const picked = lines[item.id] != null;
											const remaining = remainingOf(item);
											const partial = remaining < item.quantity;
											return (
												<div
													key={item.id}
													className="flex items-center gap-2.5 px-3 py-2.5"
													style={{
														borderTop:
															i > 0
																? '0.5px solid var(--line-1)'
																: 'none',
														background: picked
															? 'var(--accent-soft)'
															: undefined,
													}}
												>
													<button
														type="button"
														onClick={() => toggle(item)}
														title={
															picked
																? 'Leave this product out of the box'
																: 'Put this product in the box'
														}
														className="flex shrink-0 items-center"
													>
														<Radio checked={picked} />
													</button>
													<span className="min-w-0 flex-1">
														<span className="block truncate text-sm font-medium text-text-1">
															{item.productName || 'Product'}
														</span>
														<span className="mt-0.5 block font-mono text-2xs text-text-4">
															{remaining.toLocaleString()} left to
															ship
															{partial &&
																` of ${item.quantity.toLocaleString()}`}
														</span>
													</span>
													<Input
														type="number"
														min={0}
														max={remaining}
														disabled={!picked}
														value={picked ? String(lines[item.id]) : ''}
														onChange={(e) =>
															setQuantity(item, e.target.value)
														}
														className="w-[110px] text-right font-mono"
													/>
												</div>
											);
										})}
									</div>
									{notReady.length > 0 && (
										<div className="mt-2 flex flex-col gap-1">
											{notReady.map((item) => (
												<div
													key={item.id}
													className="flex items-baseline justify-between gap-3 text-xs"
												>
													<span className="min-w-0 truncate text-text-4">
														{item.productName || 'Product'}
													</span>
													<span className="shrink-0 text-text-4">
														{BLOCKED_REASON[item.status]}
													</span>
												</div>
											))}
										</div>
									)}
								</Field>

								<Field label="Carrier">
									<Input
										value={carrier}
										onChange={(e) => setCarrier(e.target.value)}
										placeholder="e.g. DHL"
									/>
									{carriers.length > 0 && (
										<div className="mt-1.5 flex flex-wrap gap-1.5">
											{carriers.map((c) => {
												const active =
													carrier.trim().toLowerCase() ===
													c.code.toLowerCase();
												return (
													<button
														key={c.code}
														type="button"
														onClick={() => setCarrier(c.code)}
														title={
															c.supportsTracking
																? 'A tracking link resolves for this carrier'
																: 'No tracking page for this carrier'
														}
														className={cn(
															'h-6 rounded-md border px-2 text-xs',
															active ? 'text-text-1' : 'text-text-3'
														)}
														style={{
															borderColor: active
																? 'var(--accent)'
																: 'var(--line-2)',
															background: active
																? 'var(--accent-soft)'
																: 'transparent',
														}}
													>
														{c.name}
													</button>
												);
											})}
										</div>
									)}
								</Field>

								<Field label="Tracking number">
									<Input
										value={trackingNumber}
										onChange={(e) => setTrackingNumber(e.target.value)}
									/>
									{/* The tracking number is what decides the status, so say
									    so before recording rather than after. */}
									<div className="mt-1.5 text-xs text-text-4">
										{trackingNumber.trim()
											? 'Recorded as shipped.'
											: 'Recorded as preparing — add tracking when the box leaves.'}
									</div>
								</Field>

								<div className="grid grid-cols-2 gap-3">
									<Field label="Shipped">
										<DatePicker
											clearable
											value={shippedAt}
											onChange={(d) => setShippedAt(d ?? null)}
										/>
									</Field>
									<Field label="Est. delivery">
										<DatePicker
											clearable
											value={estimatedDeliveryDate}
											onChange={(d) => setEstimatedDeliveryDate(d ?? null)}
										/>
									</Field>
								</div>
							</>
						)}
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={loading || chosen.length === 0}
						title={
							chosen.length === 0 ? 'Put at least one product in the box' : undefined
						}
					>
						{loading ? 'Recording…' : `Record ${totalPieces.toLocaleString()} pcs`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
